import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../config/supabase';
import { supabaseAdmin } from '../config/supabase';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { logAction, SECTIONS, ACTIONS } from '../services/auditLog';
import {
  extractStatusNotificationNote,
  normalizeStatusNote,
  notifyReportStatusChange,
} from '../utils/reportStatusNotification';
import './ReportsManagement.css';

// TODO: Replace REACT_APP_MAPBOX_ACCESS_TOKEN with your Mapbox public token (pk.xxx)
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
} else {
  console.warn('Mapbox token not set. Set REACT_APP_MAPBOX_ACCESS_TOKEN environment variable.');
}

// Style mapy – jak w mapie interaktywnej w aplikacji mobilnej
const MAP_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  satelliteStreets: 'mapbox://styles/mapbox/satellite-streets-v12',
};
const MAP_STYLE_LABELS = {
  streets: 'Mapa',
  satellite: 'Satelita',
  satelliteStreets: 'Satelita + Mapa',
};

export default function ReportsManagement() {
  const { isSupport, user } = useAuth();
  const isSupportRole = user?.role === 'support';
  const isSecretaryRole = user?.role === 'secretary';
  const canUseAdminReportActions = user?.role === 'admin';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportImages, setReportImages] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [userStatusMessages, setUserStatusMessages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('reports'); // 'reports' | 'types' | 'closed'
  const [reportTypes, setReportTypes] = useState([]);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({ name: '' });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showReportClosedPopup, setShowReportClosedPopup] = useState(false);
  const [showSupportMustTakePopup, setShowSupportMustTakePopup] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { reportId, newStatus } dla Sekretarz
  const [pendingNotificationStatusChange, setPendingNotificationStatusChange] = useState(null); // { reportId, newStatus }
  const [notificationNoteMode, setNotificationNoteMode] = useState('choice'); // 'choice' | 'note'
  const [notificationNoteText, setNotificationNoteText] = useState('');
  const [showStatusChangeSuccess, setShowStatusChangeSuccess] = useState(false);
  const [reportIdsWithRequest, setReportIdsWithRequest] = useState([]);
  const [requestFormData, setRequestFormData] = useState({
    reportId: null,
    requestedStatus: 'resolved',
    message: '',
    image: null,
  });
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const fullscreenMapRef = useRef(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [mapStyle, setMapStyle] = useState('streets');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadReports();
    loadReportTypes();
  }, []);

  const loadReportIdsWithRequest = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabaseAdmin
        .from('status_change_requests')
        .select('report_id')
        .eq('requested_by', user.id);
      if (error) throw error;
      setReportIdsWithRequest((data || []).map((r) => r.report_id));
    } catch (err) {
      console.error('Error loading status change requests:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isSupport && user?.id) {
      loadReportIdsWithRequest();
    } else {
      setReportIdsWithRequest([]);
    }
  }, [isSupport, user?.id, loadReportIdsWithRequest]);

  // Inicjalizacja mapy Mapbox (tylko gdy modal jest otwarty i report ma GPS)
  const containerRef = mapFullscreen ? fullscreenMapRef : mapDivRef;
  useEffect(() => {
    if (!selectedReport || selectedReport.location_type !== 'gps') {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setMapFullscreen(false);
      return;
    }

    const container = containerRef.current;
    if (!container) return;
    if (!selectedReport.latitude || !selectedReport.longitude) return;
    if (!MAPBOX_TOKEN) {
      console.warn('Mapbox token not set');
      return;
    }

    const lat = parseFloat(selectedReport.latitude);
    const lng = parseFloat(selectedReport.longitude);

    const map = new mapboxgl.Map({
      container,
      style: MAP_STYLES[mapStyle] || MAP_STYLES.streets,
      center: [lng, lat],
      zoom: 14,
    });

    mapRef.current = map;

    const marker = new mapboxgl.Marker()
      .setLngLat([lng, lat])
      .addTo(map);

    markerRef.current = marker;

    const ROAD_HIGHLIGHT_LAYER_ID = 'report-map-roads-highlight';

    const onLoad = () => {
      map.resize();
      // Dodanie warstwy dróg w wyróżnionym kolorze (tylko style z drogami)
      if ((mapStyle === 'streets' || mapStyle === 'satelliteStreets') && map.getSource('composite')) {
        try {
          const style = map.getStyle();
          const firstSymbolId = style?.layers?.find((l) => l.type === 'symbol')?.id;
          if (!map.getLayer(ROAD_HIGHLIGHT_LAYER_ID)) {
            map.addLayer(
              {
                id: ROAD_HIGHLIGHT_LAYER_ID,
                type: 'line',
                source: 'composite',
                'source-layer': 'road',
                minzoom: 10,
                paint: {
                  'line-color': '#f97316',
                  'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2, 14, 4, 18, 6],
                  'line-opacity': 0.85,
                },
                layout: {
                  'line-join': 'round',
                  'line-cap': 'round',
                },
              },
              firstSymbolId || undefined
            );
          }
        } catch (e) {
          console.warn('Report map: could not add road highlight layer', e);
        }
      }
    };
    map.on('load', onLoad);

    return () => {
      map.off('load', onLoad);
      try {
        if (mapRef.current?.getLayer(ROAD_HIGHLIGHT_LAYER_ID)) {
          mapRef.current.removeLayer(ROAD_HIGHLIGHT_LAYER_ID);
        }
      } catch (_) {}
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // containerRef celowo pominięty — obiekt ref jest stabilny; efekt uruchamia się przy zmianie zgłoszenia / stylu mapy
    // eslint-disable-next-line react-hooks/exhaustive-deps -- containerRef.current sprawdzany wewnątrz efektu
  }, [selectedReport, mapFullscreen, mapStyle]);

  useEffect(() => {
    if (!selectedReport) setMapFullscreen(false);
  }, [selectedReport]);

  const normalizeReportRow = (row) => {
    if (!row) return row;
    const contact = Array.isArray(row.report_contacts)
      ? row.report_contacts[0]
      : row.report_contacts;
    const { report_contacts: _rc, ...rest } = row;
    return {
      ...rest,
      phone: contact?.phone ?? '',
      email: contact?.email ?? null,
    };
  };

  const loadReports = async () => {
    try {
      const { data, error } = await supabase
        .from('reports')
        .select('*, report_contacts(phone, email)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReports((data || []).map(normalizeReportRow));
    } catch (error) {
      toast.error('Błąd podczas ładowania zgłoszeń');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadReportImages = async (reportId) => {
    try {
      const { data, error } = await supabase
        .from('report_images')
        .select('*')
        .eq('report_id', reportId);

      if (error) throw error;
      setReportImages(data || []);
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  const handleStatusChange = async (reportId, newStatus, options = {}) => {
    const { silent = false, notificationNote = '' } = options; // silent = true dla Sekretarz (pokazujemy własny popup)
    const cleanNotificationNote = normalizeStatusNote(notificationNote);
    try {
      // Pobierz zgłoszenie z user_id i statusem przed aktualizacją
      const { data: report, error: reportError } = await supabaseAdmin
        .from('reports')
        .select('user_id, title, status, report_number')
        .eq('id', reportId)
        .single();

      if (reportError) throw reportError;

      // Guard logic: push wysyłany tylko jeśli status się zmienił
      if (report.status === newStatus) {
        toast.success('Status już jest ustawiony na tę wartość');
        return;
      }

      // Aktualizuj status zgłoszenia
      const { error } = await supabaseAdmin
        .from('reports')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
          resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null,
        })
        .eq('id', reportId);

      if (error) throw error;

      await logAction(
        {
          section: SECTIONS.REPORTS,
          action: ACTIONS.STATUS_CHANGE,
          entityType: 'report',
          entityId: reportId,
          entityLabel: report?.report_number || report?.description?.slice(0, 50) || `Zgłoszenie #${reportId}`,
          details: `Status zgłoszenia: ${report.status} → ${newStatus}.`,
          extra: { oldStatus: report.status, newStatus },
        },
        user
      );

      // Zapisz historię zmian statusu
      try {
        await supabaseAdmin
          .from('report_status_history')
          .insert({
            report_id: reportId,
            old_status: report.status,
            new_status: newStatus,
            changed_at: new Date().toISOString(),
          });
      } catch (historyError) {
        console.error('Error saving status history:', historyError);
        // Nie przerywamy - status został zaktualizowany
      }

      // Zawsze zapis in-app (REST); Expo OS tylko gdy user ma włączony push (EF + skipInsert)
      if (report.user_id) {
        try {
          await notifyReportStatusChange(supabaseAdmin, {
            userId: report.user_id,
            reportId,
            reportNumber: report.report_number,
            newStatus,
            statusNote: cleanNotificationNote,
          });
        } catch (pushError) {
          console.error('Error in status notification flow:', pushError);
          toast.error('Status zmieniony, ale nie udało się zapisać powiadomienia dla użytkownika');
        }
      }

      if (!silent) toast.success('Status zaktualizowany');
      loadReports();
      if (selectedReport?.id === reportId) {
        await loadStatusHistory(reportId);
        if (report.user_id) {
          await loadUserStatusMessages(reportId, report.user_id);
        }
      }
      if (silent) setShowStatusChangeSuccess(true);
    } catch (error) {
      toast.error('Błąd podczas aktualizacji statusu');
      console.error(error);
    }
  };

  const handleSecretaryStatusSelect = (reportId, newStatus) => {
    const report = reports.find((r) => r.id === reportId);
    if (report?.status === newStatus) return;
    openStatusNotificationModal(reportId, newStatus);
  };

  const openStatusNotificationModal = (reportId, newStatus) => {
    setPendingNotificationStatusChange({ reportId, newStatus });
    setNotificationNoteMode('choice');
    setNotificationNoteText('');
  };

  const handleAdminStatusSelect = (reportId, newStatus) => {
    const report = reports.find((r) => r.id === reportId);
    if (report?.status === newStatus) return;
    openStatusNotificationModal(reportId, newStatus);
  };

  const closeStatusNotificationModal = () => {
    setPendingNotificationStatusChange(null);
    setNotificationNoteMode('choice');
    setNotificationNoteText('');
  };

  const confirmStatusNotificationChange = async (notificationNote = '') => {
    if (!pendingNotificationStatusChange) return;
    const { reportId, newStatus } = pendingNotificationStatusChange;
    closeStatusNotificationModal();
    await handleStatusChange(reportId, newStatus, { notificationNote });
    if (selectedReport?.id === reportId) {
      setSelectedReport((prev) => (prev ? { ...prev, status: newStatus } : null));
    }
  };

  const handleSecretaryStatusConfirm = async () => {
    if (!pendingStatusChange) return;
    const { reportId, newStatus } = pendingStatusChange;
    setPendingStatusChange(null);
    await handleStatusChange(reportId, newStatus, { silent: true });
    if (selectedReport?.id === reportId) {
      setSelectedReport((prev) => (prev ? { ...prev, status: newStatus } : null));
    }
  };

  const isReportClosed = (report) =>
    report?.status === 'resolved' || report?.status === 'rejected';

  const hasSupportAlreadyRequested = (reportId) =>
    reportIdsWithRequest.includes(reportId);

  const handleTakeReport = (report) => {
    if (!isSecretaryRole || report?.status !== 'new') return;
    openStatusNotificationModal(report.id, 'in_progress');
  };

  const handleRequestStatusChange = (report) => {
    if (isSupportRole && isReportClosed(report)) {
      setShowReportClosedPopup(true);
      return;
    }
    if (isSupportRole && report?.status === 'new') {
      setShowSupportMustTakePopup(true);
      return;
    }
    if (isSupportRole && hasSupportAlreadyRequested(report?.id)) {
      return;
    }
    setRequestFormData({
      reportId: report.id,
      requestedStatus: isSupportRole ? 'resolved' : (report.status === 'new' ? 'in_progress' : 'resolved'),
      message: '',
      image: null,
      imagePreview: null,
    });
    setShowRequestModal(true);
  };

  const handleSubmitStatusRequest = async (e) => {
    e.preventDefault();
    
    if (!requestFormData.message.trim()) {
      toast.error('Proszę wprowadzić wiadomość');
      return;
    }

    try {
      // Upload image if provided
      let imageUrl = null;
      if (requestFormData.image) {
        const fileExt = requestFormData.image.name.split('.').pop();
        const fileName = `status-request-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `status-requests/${fileName}`;

        const { error: uploadError } = await supabaseAdmin.storage
          .from('report-images')
          .upload(filePath, requestFormData.image);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabaseAdmin.storage
          .from('report-images')
          .getPublicUrl(filePath);
        
        imageUrl = publicUrl;
      }

      if (!user || !user.id) {
        throw new Error('Użytkownik nie jest zalogowany');
      }

      // Get current report status
      const { data: report, error: reportError } = await supabaseAdmin
        .from('reports')
        .select('status')
        .eq('id', requestFormData.reportId)
        .single();

      if (reportError) {
        console.error('Error fetching report:', reportError);
        throw new Error(`Błąd podczas pobierania zgłoszenia: ${reportError.message}`);
      }

      if (!report) {
        throw new Error('Zgłoszenie nie zostało znalezione');
      }

      // Create status change request
      const insertData = {
        report_id: requestFormData.reportId,
        requested_by: user.id,
        current_status: report.status,
        requested_status: requestFormData.requestedStatus,
        message: requestFormData.message.trim(),
        image_url: imageUrl || null,
      };

      console.log('Inserting status change request:', {
        ...insertData,
        requested_by: user.id,
        user_email: user.email,
      });

      const { error, data } = await supabaseAdmin
        .from('status_change_requests')
        .insert(insertData)
        .select();

      if (error) {
        console.error('Error inserting status change request:', error);
        console.error('Error code:', error.code);
        console.error('Error details:', error.details);
        console.error('Error hint:', error.hint);
        throw new Error(`Błąd podczas zapisywania prośby: ${error.message || error.code || 'Nieznany błąd'}`);
      }

      console.log('Status change request created successfully:', data);

      toast.success('Prośba o zmianę statusu została wysłana');
      setShowRequestModal(false);
      setReportIdsWithRequest((prev) =>
        requestFormData.reportId && !prev.includes(requestFormData.reportId)
          ? [...prev, requestFormData.reportId]
          : prev
      );
      setRequestFormData({
        reportId: null,
        requestedStatus: 'resolved',
        message: '',
        image: null,
        imagePreview: null,
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      toast.error(error.message || 'Błąd podczas wysyłania prośby');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Wybierz plik obrazu');
        return;
      }
      setRequestFormData({
        ...requestFormData,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zgłoszenie?')) return;

    try {
      const { error } = await supabaseAdmin
        .from('reports')
        .delete()
        .eq('id', reportId);

      if (error) throw error;

      toast.success('Zgłoszenie usunięte');
      loadReports();
      if (selectedReport && selectedReport.id === reportId) {
        setSelectedReport(null);
        setReportImages([]);
      }
    } catch (error) {
      toast.error('Błąd podczas usuwania zgłoszenia');
      console.error(error);
    }
  };

  const loadStatusHistory = async (reportId) => {
    try {
      const { data, error } = await supabaseAdmin
        .from('report_status_history')
        .select('*')
        .eq('report_id', reportId)
        .order('changed_at', { ascending: false });

      if (error) throw error;
      setStatusHistory(data || []);
    } catch (error) {
      console.error('Error loading status history:', error);
      setStatusHistory([]);
    }
  };

  const loadUserStatusMessages = async (reportId, userId) => {
    if (!userId) {
      setUserStatusMessages([]);
      return;
    }

    try {
      const { data, error } = await supabaseAdmin
        .from('push_notifications')
        .select('id, body, sent_at, data')
        .eq('user_id', userId)
        .eq('data->>type', 'report_status_change')
        .eq('data->>reportId', String(reportId))
        .order('sent_at', { ascending: false });

      if (error) throw error;

      const messages = (data || [])
        .map((notification) => ({
          ...notification,
          note: extractStatusNotificationNote(notification),
        }))
        .filter((notification) => notification.note.length > 0);

      setUserStatusMessages(messages);
    } catch (error) {
      console.error('Error loading user status messages:', error);
      setUserStatusMessages([]);
    }
  };

  const loadReportTypes = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('report_types')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setReportTypes(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania rodzajów zgłoszeń');
      console.error(error);
    }
  };

  const handleAddType = () => {
    setEditingType(null);
    setTypeFormData({ name: '' });
    setShowTypeModal(true);
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeFormData({ name: type.name });
    setShowTypeModal(true);
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten rodzaj zgłoszenia?')) return;

    try {
      const { error } = await supabaseAdmin
        .from('report_types')
        .delete()
        .eq('id', typeId);

      if (error) throw error;
      toast.success('Rodzaj zgłoszenia usunięty');
      loadReportTypes();
    } catch (error) {
      toast.error('Błąd podczas usuwania rodzaju zgłoszenia');
      console.error(error);
    }
  };

  const handleSaveType = async () => {
    if (!typeFormData.name.trim()) {
      toast.error('Nazwa jest wymagana');
      return;
    }

    try {
      if (editingType) {
        const { error } = await supabaseAdmin
          .from('report_types')
          .update({ name: typeFormData.name.trim() })
          .eq('id', editingType.id);

        if (error) throw error;
        toast.success('Rodzaj zgłoszenia zaktualizowany');
      } else {
        const { error } = await supabaseAdmin
          .from('report_types')
          .insert({ name: typeFormData.name.trim() });

        if (error) throw error;
        toast.success('Rodzaj zgłoszenia dodany');
      }

      setShowTypeModal(false);
      loadReportTypes();
    } catch (error) {
      toast.error('Błąd podczas zapisywania rodzaju zgłoszenia');
      console.error(error);
    }
  };

  const handleViewReport = async (report) => {
    setSelectedReport(report);
    await loadReportImages(report.id);
    await loadStatusHistory(report.id);
    await loadUserStatusMessages(report.id, report.user_id);
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: 'badge-primary',
      in_progress: 'badge-warning',
      resolved: 'badge-success',
      rejected: 'badge-danger',
    };
    return badges[status] || 'badge-info';
  };

  const getStatusName = (status) => {
    const names = {
      new: 'Nowe',
      in_progress: 'W trakcie',
      resolved: 'Rozwiązane',
      rejected: 'Odrzucone',
    };
    return names[status] || status;
  };

  const reportsMatchingSearch = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return reports;

    return reports.filter((report) => {
      const haystack = [
        report.report_number,
        report.phone,
        report.email,
        report.report_type,
        report.title,
        report.description,
        report.status,
        getStatusName(report.status),
        report.city,
        report.street,
        report.postal_code,
        report.house_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [reports, searchQuery]);

  const closedReports = reportsMatchingSearch.filter(
    (r) => r.status === 'resolved' || r.status === 'rejected'
  );
  const activeReports = reportsMatchingSearch.filter(
    (r) => r.status !== 'resolved' && r.status !== 'rejected'
  );

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="reports-management">
      <h2>Zarządzanie zgłoszeniami</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #ddd' }}>
        <button
          className={`btn ${activeTab === 'reports' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('reports')}
          style={{ marginBottom: '-2px', borderBottom: activeTab === 'reports' ? '2px solid #007bff' : 'none' }}
        >
          Zgłoszenia
        </button>
        {canUseAdminReportActions && (
          <button
            className={`btn ${activeTab === 'types' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('types')}
            style={{ marginBottom: '-2px', borderBottom: activeTab === 'types' ? '2px solid #007bff' : 'none' }}
          >
            Rodzaje zgłoszeń
          </button>
        )}
        {(canUseAdminReportActions || isSupportRole || isSecretaryRole) && (
          <button
            className={`btn ${activeTab === 'closed' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('closed')}
            style={{ marginBottom: '-2px', borderBottom: activeTab === 'closed' ? '2px solid #007bff' : 'none' }}
          >
            Zgłoszenia zakończone
          </button>
        )}
      </div>

      {(activeTab === 'reports' || activeTab === 'closed') && (
        <div className="reports-search-bar card">
          <label htmlFor="reports-search" className="reports-search-label">
            Szukaj zgłoszeń
          </label>
          <input
            id="reports-search"
            type="search"
            className="form-input reports-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Numer, telefon, e-mail, kategoria, status, opis, adres…"
          />
          {searchQuery.trim() && (
            <button
              type="button"
              className="btn btn-secondary reports-search-clear"
              onClick={() => setSearchQuery('')}
            >
              Wyczyść
            </button>
          )}
        </div>
      )}

      {/* Report Types Management */}
      {activeTab === 'types' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>Rodzaje zgłoszeń</h3>
            <button className="btn btn-primary" onClick={handleAddType}>
              Dodaj rodzaj
            </button>
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>Nazwa</th>
                <th>Data utworzenia</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {reportTypes.length === 0 ? (
                <tr>
                  <td colSpan="3" className="empty-state">Brak rodzajów zgłoszeń</td>
                </tr>
              ) : (
                reportTypes.map((type) => (
                  <tr key={type.id}>
                    <td>{type.name}</td>
                    <td>{new Date(type.created_at).toLocaleDateString('pl-PL')}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleEditType(type)}
                          style={{ marginRight: '8px' }}
                        >
                          Edytuj
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteType(type.id)}
                        >
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reports Management – tylko zgłoszenia aktywne (Nowe, W trakcie) */}
      {activeTab === 'reports' && (
        <>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Numer</th>
              <th>Tytuł</th>
              <th>Status</th>
              <th>Lokalizacja</th>
              <th>Data</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {activeReports.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  {searchQuery.trim() ? 'Brak zgłoszeń pasujących do wyszukiwania' : 'Brak zgłoszeń'}
                </td>
              </tr>
            ) : (
              activeReports.map((report) => (
                <tr key={report.id}>
                  <td>{report.report_number || '—'}</td>
                  <td>{report.report_type || report.title || 'Brak tytułu'}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(report.status)}`}>
                      {getStatusName(report.status)}
                    </span>
                  </td>
                  <td>
                    {report.location_type === 'gps' 
                      ? 'GPS' 
                      : `${report.city || ''} ${report.street || ''} ${report.house_number || ''}`.trim()}
                  </td>
                  <td>{new Date(report.created_at).toLocaleDateString('pl-PL')}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleViewReport(report)}
                      >
                        Zobacz
                      </button>
                      {canUseAdminReportActions && (
                        <>
                          <select
                            className="form-input status-select"
                            value={report.status}
                            onChange={(e) => handleAdminStatusSelect(report.id, e.target.value)}
                          >
                            <option value="new">Nowe</option>
                            <option value="in_progress">W trakcie</option>
                            <option value="resolved">Rozwiązane</option>
                            <option value="rejected">Odrzucone</option>
                          </select>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDelete(report.id)}
                            style={{ marginLeft: '8px' }}
                          >
                            Usuń
                          </button>
                        </>
                      )}
                      {isSecretaryRole && report.status === 'new' && (
                        <button
                          className="btn btn-primary"
                          onClick={() => handleTakeReport(report)}
                          style={{ marginLeft: '8px' }}
                          title="Podejmij zgłoszenie (status zmieni się na „W trakcie”)"
                        >
                          Podejmij zgłoszenie
                        </button>
                      )}
                      {isSecretaryRole && report.status !== 'new' && (
                        <select
                          className="form-input status-select"
                          value={report.status}
                          onChange={(e) => handleSecretaryStatusSelect(report.id, e.target.value)}
                          style={{ marginLeft: '8px' }}
                        >
                          {report.status === 'in_progress' && (
                            <option value="in_progress" disabled>W trakcie (aktualny)</option>
                          )}
                          <option value="resolved">Rozwiązane</option>
                          <option value="rejected">Odrzucone</option>
                        </select>
                      )}
                      {isSupportRole && report.status === 'in_progress' && (
                        <>
                          <button
                            className={hasSupportAlreadyRequested(report.id) ? 'btn btn-secondary btn-disabled' : 'btn btn-secondary'}
                            onClick={() => !hasSupportAlreadyRequested(report.id) && handleRequestStatusChange(report)}
                            style={{ marginLeft: '8px' }}
                            title={hasSupportAlreadyRequested(report.id) ? 'Prośba o zmianę statusu została już wysłana' : undefined}
                          >
                            Wyślij prośbę
                          </button>
                          {hasSupportAlreadyRequested(report.id) && (
                            <span className="support-request-sent-msg" style={{ marginLeft: '8px', fontSize: '13px', color: '#fff' }}>
                              Prośba o zmianę statusu została już wysłana
                            </span>
                          )}
                        </>
                      )}
                      {isSupportRole && isReportClosed(report) && (
                        <button
                          className="btn btn-secondary btn-disabled"
                          onClick={() => handleRequestStatusChange(report)}
                          style={{ marginLeft: '8px' }}
                          title="Zgłoszenie zakończone – nie można wysłać prośby"
                        >
                          Wyślij prośbę
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
        </>
      )}

      {/* Zgłoszenia zakończone (rozwiązane + odrzucone) */}
      {activeTab === 'closed' && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Numer</th>
                <th>Tytuł</th>
                <th>Status</th>
                <th>Lokalizacja</th>
                <th>Data</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {closedReports.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state">
                    {searchQuery.trim() ? 'Brak zgłoszeń pasujących do wyszukiwania' : 'Brak zgłoszeń zakończonych'}
                  </td>
                </tr>
              ) : (
                closedReports.map((report) => (
                  <tr key={report.id}>
                    <td>{report.report_number || '—'}</td>
                    <td>{report.report_type || report.title || 'Brak tytułu'}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(report.status)}`}>
                        {getStatusName(report.status)}
                      </span>
                    </td>
                    <td>
                      {report.location_type === 'gps'
                        ? 'GPS'
                        : `${report.city || ''} ${report.street || ''} ${report.house_number || ''}`.trim()}
                    </td>
                    <td>{new Date(report.created_at).toLocaleDateString('pl-PL')}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleViewReport(report)}
                        >
                          Zobacz
                        </button>
                        {canUseAdminReportActions && (
                          <>
                            <select
                              className="form-input status-select"
                              value={report.status}
                              onChange={(e) => handleAdminStatusSelect(report.id, e.target.value)}
                              style={{ marginLeft: '8px' }}
                            >
                              <option value="new">Nowe</option>
                              <option value="in_progress">W trakcie</option>
                              <option value="resolved">Rozwiązane</option>
                              <option value="rejected">Odrzucone</option>
                            </select>
                            <button
                              className="btn btn-danger"
                              onClick={() => handleDelete(report.id)}
                              style={{ marginLeft: '8px' }}
                            >
                              Usuń
                            </button>
                          </>
                        )}
                        {isSupportRole && (
                          <button
                            className="btn btn-secondary btn-disabled"
                            onClick={() => handleRequestStatusChange(report)}
                            style={{ marginLeft: '8px' }}
                            title="Zgłoszenie zakończone – nie można wysłać prośby"
                          >
                            Wyślij prośbę
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedReport && (
        <div className="modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="modal-content report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {selectedReport.report_number
                  ? `${selectedReport.report_number} — ${selectedReport.report_type || selectedReport.title || 'Zgłoszenie'}`
                  : (selectedReport.report_type || selectedReport.title || 'Zgłoszenie')}
              </h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {canUseAdminReportActions && (
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(selectedReport.id)}
                  >
                    Usuń
                  </button>
                )}
                {isSecretaryRole && selectedReport.status === 'new' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleTakeReport(selectedReport)}
                    title="Podejmij zgłoszenie (status zmieni się na „W trakcie”)"
                  >
                    Podejmij zgłoszenie
                  </button>
                )}
                {isSecretaryRole && selectedReport.status !== 'new' && (
                  <select
                    className="form-input status-select"
                    value={selectedReport.status}
                    onChange={(e) => handleSecretaryStatusSelect(selectedReport.id, e.target.value)}
                  >
                    {selectedReport.status === 'in_progress' && (
                      <option value="in_progress" disabled>W trakcie (aktualny)</option>
                    )}
                    <option value="resolved">Rozwiązane</option>
                    <option value="rejected">Odrzucone</option>
                  </select>
                )}
                {isSupportRole && selectedReport.status === 'in_progress' && (
                  <>
                    <button
                      className={hasSupportAlreadyRequested(selectedReport.id) ? 'btn btn-secondary btn-disabled' : 'btn btn-secondary'}
                      onClick={() => !hasSupportAlreadyRequested(selectedReport.id) && handleRequestStatusChange(selectedReport)}
                      title={hasSupportAlreadyRequested(selectedReport.id) ? 'Prośba o zmianę statusu została już wysłana' : undefined}
                    >
                      Wyślij prośbę o zmianę statusu
                    </button>
                    {hasSupportAlreadyRequested(selectedReport.id) && (
                      <span className="support-request-sent-msg" style={{ marginLeft: '8px', fontSize: '13px', color: '#fff' }}>
                        Prośba o zmianę statusu została już wysłana
                      </span>
                    )}
                  </>
                )}
                {isSupportRole && isReportClosed(selectedReport) && (
                  <button
                    className="btn btn-secondary btn-disabled"
                    onClick={() => handleRequestStatusChange(selectedReport)}
                    title="Zgłoszenie zakończone – nie można wysłać prośby"
                  >
                    Wyślij prośbę o zmianę statusu
                  </button>
                )}
                <button
                  className="modal-close"
                  onClick={() => setSelectedReport(null)}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="report-details">
              {selectedReport.report_number && (
                <div className="report-section">
                  <h4>Numer zgłoszenia</h4>
                  <p className="report-number-value">{selectedReport.report_number}</p>
                </div>
              )}
              <div className="report-section">
                <h4>Opis</h4>
                <p>{selectedReport.description}</p>
              </div>
              {selectedReport.phone && String(selectedReport.phone).trim() !== '' && (
                <div className="report-section">
                  <h4>Telefon</h4>
                  <p>{selectedReport.phone}</p>
                </div>
              )}
              {selectedReport.email && String(selectedReport.email).trim() !== '' && (
                <div className="report-section">
                  <h4>E-mail</h4>
                  <p>
                    <a href={`mailto:${selectedReport.email}`}>{selectedReport.email}</a>
                  </p>
                </div>
              )}
              <div className="report-section">
                <h4>Lokalizacja</h4>
                {selectedReport.location_type === 'gps' ? (
                  <div>
                    {/* Full address text (even for GPS) */}
                    {(selectedReport.city || selectedReport.street || selectedReport.house_number) && (
                      <p style={{ marginBottom: '12px' }}>
                        {selectedReport.city && `${selectedReport.city}`}
                        {selectedReport.postal_code && `, ${selectedReport.postal_code}`}
                        {selectedReport.street && `, ${selectedReport.street}`}
                        {selectedReport.house_number && ` ${selectedReport.house_number}`}
                        {selectedReport.apartment_number && `/${selectedReport.apartment_number}`}
                      </p>
                    )}
                    {/* Map */}
                    {selectedReport.latitude && selectedReport.longitude && (
                      <div className="map-container" style={{ height: '300px', width: '100%', marginTop: '10px', position: 'relative' }}>
                        {!MAPBOX_TOKEN ? (
                          <div style={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            color: '#ff3b30'
                          }}>
                            Brak tokenu Mapbox – ustaw REACT_APP_MAPBOX_ACCESS_TOKEN
                          </div>
                        ) : (
                          <>
                            <div ref={mapDivRef} style={{ height: '100%', width: '100%', display: mapFullscreen ? 'none' : 'block' }} />
                            <div className="map-style-switcher">
                              {Object.entries(MAP_STYLE_LABELS).map(([key, label]) => (
                                <button
                                  key={key}
                                  type="button"
                                  className={`map-style-btn ${mapStyle === key ? 'map-style-btn-active' : ''}`}
                                  onClick={() => setMapStyle(key)}
                                  title={label}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <button
                              type="button"
                              className="map-fullscreen-btn"
                              onClick={() => setMapFullscreen(true)}
                              title="Mapa na pełny ekran"
                            >
                              Pełny ekran
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p>
                    {selectedReport.city && `${selectedReport.city}`}
                    {selectedReport.postal_code && `, ${selectedReport.postal_code}`}
                    {selectedReport.street && `, ${selectedReport.street}`}
                    {selectedReport.house_number && ` ${selectedReport.house_number}`}
                    {selectedReport.apartment_number && `/${selectedReport.apartment_number}`}
                  </p>
                )}
              </div>
              {reportImages.length > 0 && (
                <div className="report-section">
                  <h4>Zdjęcia</h4>
                  <div className="images-grid">
                    {reportImages.map((img) => (
                      <img
                        key={img.id}
                        src={img.image_url}
                        alt="Zgłoszenie"
                        className="report-image"
                        onClick={() => setSelectedImage(img.image_url)}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                  </div>
                </div>
              )}
              {userStatusMessages.length > 0 && (
                <div className="report-section">
                  <h4>Wiadomości wysłane do użytkownika</h4>
                  <div className="user-status-messages">
                    {userStatusMessages.map((message) => (
                      <div key={message.id} className="user-status-message">
                        <div className="user-status-message-date">
                          {new Date(message.sent_at).toLocaleString('pl-PL')}
                        </div>
                        <div className="user-status-message-text">{message.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {statusHistory.length > 0 && (
                <div className="report-section">
                  <h4>Historia zmian statusu</h4>
                  <table className="table" style={{ marginTop: '10px' }}>
                    <thead>
                      <tr>
                        <th>Poprzedni status</th>
                        <th>Nowy status</th>
                        <th>Data zmiany</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusHistory.map((historyItem) => (
                        <tr key={historyItem.id}>
                          <td>
                            {historyItem.old_status ? (
                              <span className={`badge ${getStatusBadge(historyItem.old_status)}`}>
                                {getStatusName(historyItem.old_status)}
                              </span>
                            ) : (
                              <span className="badge badge-secondary">-</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadge(historyItem.new_status)}`}>
                              {getStatusName(historyItem.new_status)}
                            </span>
                          </td>
                          <td>{new Date(historyItem.changed_at).toLocaleString('pl-PL')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
          <div className="modal-content image-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setSelectedImage(null)}
              style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1001 }}
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Zgłoszenie"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto'
              }}
            />
          </div>
        </div>
      )}

      {/* Report Type Modal */}
      {showTypeModal && (
        <div className="modal-overlay" onClick={() => setShowTypeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingType ? 'Edytuj rodzaj zgłoszenia' : 'Dodaj rodzaj zgłoszenia'}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowTypeModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Nazwa *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={typeFormData.name}
                  onChange={(e) => setTypeFormData({ name: e.target.value })}
                  placeholder="Nazwa rodzaju zgłoszenia"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTypeModal(false)}
                >
                  Anuluj
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveType}
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Administrator / Obsługa: opcjonalna informacja dla użytkownika przy zmianie statusu */}
      {pendingNotificationStatusChange && (
        <div className="modal-overlay" onClick={closeStatusNotificationModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3>Informacja dla użytkownika</h3>
              <button className="modal-close" onClick={closeStatusNotificationModal}>
                ×
              </button>
            </div>

            {notificationNoteMode === 'choice' ? (
              <>
                <p style={{ padding: '16px 0', margin: 0 }}>
                  Możesz zmienić status bez dodatkowej informacji albo dodać wiadomość, która pojawi się w powiadomieniu użytkownika.
                </p>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeStatusNotificationModal}
                  >
                    Anuluj
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => confirmStatusNotificationChange('')}
                  >
                    Zmień status bez informacji
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setNotificationNoteMode('note')}
                  >
                    Dodaj informację dla użytkownika
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Informacja dla użytkownika *</label>
                  <textarea
                    className="form-textarea"
                    value={notificationNoteText}
                    onChange={(e) => setNotificationNoteText(e.target.value)}
                    placeholder="Wpisz informację, która zostanie dołączona do powiadomienia..."
                    rows={5}
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setNotificationNoteMode('choice')}
                  >
                    Wróć
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const cleanNote = normalizeStatusNote(notificationNoteText);
                      if (!cleanNote) {
                        toast.error('Wpisz informację dla użytkownika albo wróć i wybierz zmianę bez informacji');
                        return;
                      }
                      confirmStatusNotificationChange(cleanNote);
                    }}
                  >
                    Zmień status i wyślij informację
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Status Change Request Modal (for support role) */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Wyślij prośbę o zmianę statusu</h3>
              <button className="modal-close" onClick={() => setShowRequestModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmitStatusRequest}>
              <div className="form-group">
                <label className="form-label">Nowy status *</label>
                <select
                  className="form-input"
                  value={requestFormData.requestedStatus}
                  onChange={(e) => setRequestFormData({ ...requestFormData, requestedStatus: e.target.value })}
                  required
                >
                  <option value="resolved">Rozwiązane</option>
                  <option value="rejected">Odrzucone</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Wiadomość dla administratora i użytkownika *</label>
                <textarea
                  className="form-textarea"
                  value={requestFormData.message}
                  onChange={(e) => setRequestFormData({ ...requestFormData, message: e.target.value })}
                  placeholder="Opisz sytuację. Po zatwierdzeniu zmiany statusu ta wiadomość trafi też do użytkownika w powiadomieniu."
                  required
                  rows={5}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Zdjęcie rozwiązania (opcjonalne)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="form-input"
                />
                {requestFormData.imagePreview && (
                  <div style={{ marginTop: '12px' }}>
                    <img
                      src={requestFormData.imagePreview}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => setRequestFormData({ ...requestFormData, image: null, imagePreview: null })}
                      style={{ marginTop: '8px' }}
                    >
                      Usuń zdjęcie
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRequestModal(false)}
                >
                  Anuluj
                </button>
                <button type="submit" className="btn btn-primary">
                  Wyślij prośbę
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sekretarz: potwierdzenie zmiany statusu */}
      {pendingStatusChange && (
        <div className="modal-overlay" onClick={() => setPendingStatusChange(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Zmiana statusu</h3>
              <button className="modal-close" onClick={() => setPendingStatusChange(null)}>
                ×
              </button>
            </div>
            <p style={{ padding: '16px 0', margin: 0 }}>
              Czy na pewno chcesz zmienić status zgłoszenia?
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPendingStatusChange(null)}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSecretaryStatusConfirm}
              >
                Tak, zmień
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sekretarz: potwierdzenie po zmianie statusu */}
      {showStatusChangeSuccess && (
        <div className="modal-overlay" onClick={() => setShowStatusChangeSuccess(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Status zmieniony</h3>
              <button className="modal-close" onClick={() => setShowStatusChangeSuccess(false)}>
                ×
              </button>
            </div>
            <p style={{ padding: '16px 0', margin: 0 }}>
              Status zgłoszenia został zmieniony.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowStatusChangeSuccess(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup: Obsługa – najpierw trzeba podjąć zgłoszenie (status Nowe) */}
      {showSupportMustTakePopup && (
        <div className="modal-overlay" onClick={() => setShowSupportMustTakePopup(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Nie można wysłać prośby</h3>
              <button className="modal-close" onClick={() => setShowSupportMustTakePopup(false)}>
                ×
              </button>
            </div>
            <p style={{ padding: '16px 0', margin: 0 }}>
              Zgłoszenie ma status „Nowe”. Najpierw podjąć zgłoszenie przyciskiem „Podejmij zgłoszenie” (status zmieni się na „W trakcie”), a dopiero potem można wysłać do administratora prośbę o zmianę statusu.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowSupportMustTakePopup(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup: zgłoszenie zakończone – Obsługa nie może wysłać prośby */}
      {showReportClosedPopup && (
        <div className="modal-overlay" onClick={() => setShowReportClosedPopup(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Nie można wysłać prośby</h3>
              <button className="modal-close" onClick={() => setShowReportClosedPopup(false)}>
                ×
              </button>
            </div>
            <p style={{ padding: '16px 0', margin: 0 }}>
              Nie można wysłać prośby o zmianę statusu, ponieważ zgłoszenie jest zakończone (rozwiązane lub odrzucone).
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowReportClosedPopup(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mapa Mapbox na pełny ekran */}
      {mapFullscreen && selectedReport?.location_type === 'gps' && selectedReport?.latitude && selectedReport?.longitude && MAPBOX_TOKEN && (
        <div className="map-fullscreen-overlay">
          <div ref={fullscreenMapRef} className="map-fullscreen-container" />
          <div className="map-fullscreen-style-switcher">
            {Object.entries(MAP_STYLE_LABELS).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`map-style-btn map-style-btn-fullscreen ${mapStyle === key ? 'map-style-btn-active' : ''}`}
                onClick={() => setMapStyle(key)}
                title={label}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="map-fullscreen-close"
            onClick={() => setMapFullscreen(false)}
            title="Zamknij pełny ekran"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

